import { useMemo } from 'react';

import { TypedPolicy } from '../../../types';

import {
  DockerRegistryFormData,
  DockerRegistryPolicyPayload,
  DOCKER_REGISTRY_INITIAL_VALUES,
} from './types';

/**
 * Hook to get initial values for Docker Registry Policy form
 *
 * @param policy - Optional policy to load values from (for edit mode)
 * @returns Form initial values
 */
export function useDockerRegistryInitialValues(
  policy?: TypedPolicy<unknown>
): DockerRegistryFormData {
  return useMemo(() => {
    // Create mode: use defaults
    if (!policy) {
      return DOCKER_REGISTRY_INITIAL_VALUES;
    }

    // Edit mode: deserialize from policy data
    const data = policy.Data;
    if (!data || typeof data !== 'object') {
      return DOCKER_REGISTRY_INITIAL_VALUES;
    }
    if (!('RegistryAccessPolicies' in data)) {
      return DOCKER_REGISTRY_INITIAL_VALUES;
    }

    const policyData = data as DockerRegistryPolicyPayload;
    const accessRows: DockerRegistryFormData['accessRows'] = [];

    // Convert per-registry structure to form rows
    const regPolicies = policyData.RegistryAccessPolicies;
    if (regPolicies) {
      Object.entries(regPolicies).forEach(([registryIdStr, registryPolicy]) => {
        const registryId = parseInt(registryIdStr, 10);
        const userIds: number[] = [];
        const teamIds: number[] = [];

        // Extract user IDs
        if (registryPolicy.UserAccessPolicies) {
          Object.keys(registryPolicy.UserAccessPolicies).forEach((userId) => {
            userIds.push(parseInt(userId, 10));
          });
        }

        // Extract team IDs
        if (registryPolicy.TeamAccessPolicies) {
          Object.keys(registryPolicy.TeamAccessPolicies).forEach((teamId) => {
            teamIds.push(parseInt(teamId, 10));
          });
        }

        // Only add row if there's at least one user or team
        if (userIds.length > 0 || teamIds.length > 0) {
          accessRows.push({
            registryId,
            userIds,
            teamIds,
          });
        }
      });
    }

    return {
      accessRows,
    };
  }, [policy]);
}
